/**
 * QrCodeFrameService
 * Service for QR code frame management
 * Route: api/codes/qr-frames
 * Controller: QrCodeFramesController.cs
 */
import { BaseService } from '../base/BaseService';
/**
 * QrCodeFrameService
 * Handles CRUD operations for QR code frames
 */
export class QrCodeFrameService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? QrCodeFrameService.DEFAULT_SERVICE_PATH,
            basePath: QrCodeFrameService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== READ OPERATIONS ====================
    /**
     * Get all QR code frames
     * GET api/codes/qr-frames
     */
    async getAll() {
        const response = await this.getAllRaw();
        return response.data ?? [];
    }
    /**
     * Get all frames with full response
     */
    getAllRaw() {
        const url = this.buildUrl();
        return this.http.get(url);
    }
    /**
     * Get a single QR code frame by ID
     * GET api/codes/qr-frames/{id}
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(response.message || 'QR code frame not found');
        }
        return response.data;
    }
    /**
     * Get frame by ID with full response
     */
    getByIdRaw(id) {
        const endpoint = this.getEndpoint('get-by-id', `${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== WRITE OPERATIONS ====================
    /**
     * Create a new QR code frame
     * POST api/codes/qr-frames
     */
    async create(frame) {
        const response = await this.createRaw(frame);
        if (!response.data) {
            throw new Error(response.message || 'Failed to create QR code frame');
        }
        return response.data;
    }
    /**
     * Create frame with full response
     */
    createRaw(frame) {
        const url = this.buildUrl();
        return this.http.post(url, frame);
    }
    /**
     * Update an existing QR code frame
     * PUT api/codes/qr-frames/{id}
     */
    async update(id, frame) {
        const response = await this.updateRaw(id, frame);
        if (!response.data) {
            throw new Error(response.message || 'Failed to update QR code frame');
        }
        return response.data;
    }
    /**
     * Update frame with full response
     */
    updateRaw(id, frame) {
        const endpoint = this.getEndpoint('update', `${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, frame);
    }
    /**
     * Delete a QR code frame
     * DELETE api/codes/qr-frames/{id}
     */
    async delete(id) {
        const response = await this.deleteRaw(id);
        if (!response.success) {
            throw new Error(response.message || 'Failed to delete QR code frame');
        }
    }
    /**
     * Delete frame with full response
     */
    deleteRaw(id) {
        const endpoint = this.getEndpoint('delete', `${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.delete(url);
    }
}
QrCodeFrameService.DEFAULT_SERVICE_PATH = 'code-generators';
QrCodeFrameService.BASE_PATH = 'api/codes/qr-frames';
//# sourceMappingURL=QrCodeFrameService.js.map